/**
 * 应用配置状态模块
 * @description 管理应用的配置设置，如主题、布局、偏好等
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { safeLocalStorage } from '@/utils/storage'

export interface SettingsState {
  theme: 'light' | 'dark' | 'auto'
  primaryColor: string
  fontSize: 'small' | 'medium' | 'large'
  layout: 'default' | 'compact' | 'comfortable'
  sidebarCollapsed: boolean
  showBreadcrumb: boolean
  showTabs: boolean
  enableTransition: boolean
  enableWatermark: boolean
  watermarkText: string
}

export const useSettingsStore = defineStore(
  'settings',
  () => {
    // 状态定义
    const theme = ref<'light' | 'dark' | 'auto'>('light')
    const primaryColor = ref('#1f4e79') // Mech3D主色调
    const fontSize = ref<'small' | 'medium' | 'large'>('medium')
    const layout = ref<'default' | 'compact' | 'comfortable'>('default')
    const sidebarCollapsed = ref(false)
    const showBreadcrumb = ref(true)
    const showTabs = ref(true)
    const enableTransition = ref(true)
    const enableWatermark = ref(false)
    const watermarkText = ref('Mech3D')

    // 计算属性
    const isDarkMode = computed(() => {
      if (theme.value === 'auto') {
        // 检测系统主题
        return window.matchMedia('(prefers-color-scheme: dark)').matches
      }
      return theme.value === 'dark'
    })

    // To hold the reference to the media query listener function
    let mediaQueryListener: ((e: MediaQueryListEvent) => void) | null = null

    const isLightMode = computed(() => !isDarkMode.value)

    const currentThemeClass = computed(() => {
      return isDarkMode.value ? 'dark' : 'light'
    })

    const fontSizeValue = computed(() => {
      const sizeMap = {
        small: '12px',
        medium: '14px',
        large: '16px',
      }
      return sizeMap[fontSize.value]
    })

    const layoutConfig = computed(() => {
      const configs = {
        default: {
          padding: '16px',
          spacing: '16px',
          borderRadius: '6px',
        },
        compact: {
          padding: '8px',
          spacing: '8px',
          borderRadius: '4px',
        },
        comfortable: {
          padding: '24px',
          spacing: '24px',
          borderRadius: '8px',
        },
      }
      return configs[layout.value]
    })

    // 操作方法
    function setTheme(newTheme: 'light' | 'dark' | 'auto') {
      theme.value = newTheme
      applyTheme()
    }

    function toggleTheme() {
      if (theme.value === 'light') {
        setTheme('dark')
      }
      else if (theme.value === 'dark') {
        setTheme('auto')
      }
      else {
        setTheme('light')
      }
    }

    function setPrimaryColor(color: string) {
      primaryColor.value = color
      applyPrimaryColor()
    }

    function setFontSize(size: 'small' | 'medium' | 'large') {
      fontSize.value = size
      applyFontSize()
    }

    function setLayout(newLayout: 'default' | 'compact' | 'comfortable') {
      layout.value = newLayout
      applyLayout()
    }

    function setSidebarCollapsed(collapsed: boolean) {
      sidebarCollapsed.value = collapsed
    }

    function toggleSidebar() {
      sidebarCollapsed.value = !sidebarCollapsed.value
    }

    function setShowBreadcrumb(show: boolean) {
      showBreadcrumb.value = show
    }

    function setShowTabs(show: boolean) {
      showTabs.value = show
    }

    function setEnableTransition(enable: boolean) {
      enableTransition.value = enable
      applyTransition()
    }

    function setEnableWatermark(enable: boolean) {
      enableWatermark.value = enable
      applyWatermark()
    }

    function setWatermarkText(text: string) {
      watermarkText.value = text
      if (enableWatermark.value) {
        applyWatermark()
      }
    }

    // 应用主题
    function applyTheme() {
      const root = document.documentElement

      // Update data-theme for CSS variables
      root.setAttribute('data-theme', currentThemeClass.value)

      // 更新Element Plus主题
      if (currentThemeClass.value === 'dark') {
        root.classList.add('dark')
      }
      else {
        root.classList.remove('dark')
      }

      // Ensure theme-transition is present for smooth switching
      if (!root.classList.contains('theme-transition')) {
        // Wait a tick before adding the transition class so initial load doesn't transition
        setTimeout(() => {
          root.classList.add('theme-transition')
        }, 50)
      }

      // Update media query listener if theme is auto
      setupThemeListener()
    }

    function setupThemeListener() {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

      // Remove old listener if exists
      if (mediaQueryListener) {
        mediaQuery.removeEventListener('change', mediaQueryListener)
        mediaQueryListener = null
      }

      // Add new listener if theme is auto
      if (theme.value === 'auto') {
        mediaQueryListener = (e: MediaQueryListEvent) => {
          // Re-apply theme when system theme changes
          const root = document.documentElement
          const isDark = e.matches
          const newThemeClass = isDark ? 'dark' : 'light'
          root.setAttribute('data-theme', newThemeClass)
          if (isDark) {
            root.classList.add('dark')
          }
          else {
            root.classList.remove('dark')
          }
        }
        mediaQuery.addEventListener('change', mediaQueryListener)
      }
    }

    // 应用主色调
    function applyPrimaryColor() {
      const root = document.documentElement
      root.style.setProperty('--el-color-primary', primaryColor.value)
      root.style.setProperty('--primary', primaryColor.value)
    }

    // 应用字体大小
    function applyFontSize() {
      const root = document.documentElement
      root.style.setProperty('--el-font-size-base', fontSizeValue.value)
      root.style.setProperty('--font-body', fontSizeValue.value)
    }

    // 应用布局
    function applyLayout() {
      const root = document.documentElement
      const config = layoutConfig.value

      root.style.setProperty('--layout-padding', config.padding)
      root.style.setProperty('--layout-spacing', config.spacing)
      root.style.setProperty('--layout-border-radius', config.borderRadius)
    }

    // 应用过渡动画
    function applyTransition() {
      const root = document.documentElement
      if (enableTransition.value) {
        root.classList.add('enable-transition')
      }
      else {
        root.classList.remove('enable-transition')
      }
    }

    // 应用水印
    function applyWatermark() {
      const existingWatermark = document.getElementById('app-watermark')
      if (existingWatermark) {
        existingWatermark.remove()
      }

      if (!enableWatermark.value)
        return

      const watermark = document.createElement('div')
      watermark.id = 'app-watermark'
      watermark.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
      background-image: repeating-linear-gradient(
        45deg,
        transparent,
        transparent 100px,
        rgba(0,0,0,0.05) 100px,
        rgba(0,0,0,0.05) 200px
      );
    `

      const text = document.createElement('div')
      text.textContent = watermarkText.value
      text.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(-45deg);
      font-size: 48px;
      color: rgba(0,0,0,0.1);
      font-weight: bold;
      user-select: none;
    `

      watermark.appendChild(text)
      document.body.appendChild(watermark)
    }

    // 重置所有设置
    function resetSettings() {
      theme.value = 'light'
      primaryColor.value = '#1f4e79'
      fontSize.value = 'medium'
      layout.value = 'default'
      sidebarCollapsed.value = false
      showBreadcrumb.value = true
      showTabs.value = true
      enableTransition.value = true
      enableWatermark.value = false
      watermarkText.value = 'Mech3D'

      // 应用重置后的设置
      applyAllSettings()
    }

    // 应用所有设置
    function applyAllSettings() {
      applyTheme()
      applyPrimaryColor()
      applyFontSize()
      applyLayout()
      applyTransition()
      applyWatermark()
    }

    // 初始化设置
    function initSettings() {
      // 应用所有设置
      applyAllSettings()
    }

    // 导出设置
    function exportSettings() {
      return {
        theme: theme.value,
        primaryColor: primaryColor.value,
        fontSize: fontSize.value,
        layout: layout.value,
        sidebarCollapsed: sidebarCollapsed.value,
        showBreadcrumb: showBreadcrumb.value,
        showTabs: showTabs.value,
        enableTransition: enableTransition.value,
        enableWatermark: enableWatermark.value,
        watermarkText: watermarkText.value,
      }
    }

    // 导入设置
    function importSettings(settings: Partial<SettingsState>) {
      if (settings.theme)
        theme.value = settings.theme
      if (settings.primaryColor)
        primaryColor.value = settings.primaryColor
      if (settings.fontSize)
        fontSize.value = settings.fontSize
      if (settings.layout)
        layout.value = settings.layout
      if (typeof settings.sidebarCollapsed === 'boolean')
        sidebarCollapsed.value = settings.sidebarCollapsed
      if (typeof settings.showBreadcrumb === 'boolean')
        showBreadcrumb.value = settings.showBreadcrumb
      if (typeof settings.showTabs === 'boolean')
        showTabs.value = settings.showTabs
      if (typeof settings.enableTransition === 'boolean')
        enableTransition.value = settings.enableTransition
      if (typeof settings.enableWatermark === 'boolean')
        enableWatermark.value = settings.enableWatermark
      if (settings.watermarkText)
        watermarkText.value = settings.watermarkText

      applyAllSettings()
    }

    return {
      // 状态
      theme,
      primaryColor,
      fontSize,
      layout,
      sidebarCollapsed,
      showBreadcrumb,
      showTabs,
      enableTransition,
      enableWatermark,
      watermarkText,

      // 计算属性
      isDarkMode,
      isLightMode,
      currentThemeClass,
      fontSizeValue,
      layoutConfig,

      // 方法
      setTheme,
      toggleTheme,
      setPrimaryColor,
      setFontSize,
      setLayout,
      setSidebarCollapsed,
      toggleSidebar,
      setShowBreadcrumb,
      setShowTabs,
      setEnableTransition,
      setEnableWatermark,
      setWatermarkText,
      resetSettings,
      applyAllSettings,
      initSettings,
      exportSettings,
      importSettings,
    }
  },
  {
    persist: {
      key: 'mech3d-settings',
      storage: safeLocalStorage,
      paths: ['theme', 'primaryColor', 'fontSize', 'layout', 'sidebarCollapsed', 'showBreadcrumb', 'showTabs', 'enableTransition'],
    },
  },
)
